/**
 * Entry point. Order matters:
 *   1. Fetch runtime config (/config): it decides which auth provider to use.
 *   2. Build that provider (MSAL needs initialize() before rendering).
 *   3. Render.
 */

import { type AccountInfo, EventType, PublicClientApplication } from "@azure/msal-browser";
import { type ReactNode, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./api/client"; // installs API error handling before any request
import { App, type AuthProviderComponent } from "./app/App";
import { DevAuthProvider } from "./auth/dev/DevAuthProvider";
import { MsalAuthProvider } from "./auth/msal/MsalAuthProvider";
import { buildMsalConfig } from "./auth/msal/msal";
import { initRuntimeConfig, type RuntimeConfig } from "./config/runtime";
import { i18n, initI18n } from "./i18n/i18n";
import { initTelemetry, reportError } from "./telemetry/telemetry";
import "./index.css";

async function createAuthProvider(config: RuntimeConfig): Promise<AuthProviderComponent> {
  switch (config.authProvider) {
    case "dev":
      console.warn("[auth] Dev persona auth is active. Never use AUTH_PROVIDER=dev outside local development.");
      return DevAuthProvider;
    case "microsoft": {
      const msal = new PublicClientApplication(buildMsalConfig(config));
      await msal.initialize();
      const [firstAccount] = msal.getAllAccounts();
      if (!msal.getActiveAccount() && firstAccount) msal.setActiveAccount(firstAccount);
      msal.addEventCallback((event) => {
        // v5: LOGIN_SUCCESS payload is AccountInfo (was AuthenticationResult in v3/v4).
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          msal.setActiveAccount(event.payload as AccountInfo);
        }
      });
      return ({ children }: { children: ReactNode }) => <MsalAuthProvider instance={msal}>{children}</MsalAuthProvider>;
    }
    default:
      throw new Error(`Auth provider "${config.authProvider}" is not supported by web/ yet.`);
  }
}

async function bootstrap() {
  initI18n();
  const config = await initRuntimeConfig();
  initTelemetry(config, __APP_VERSION__);
  const AuthProvider = await createAuthProvider(config);

  const rootEl = document.getElementById("root");
  if (!rootEl) throw new Error("Missing #root element");
  createRoot(rootEl, {
    // React 19: errors React caught (error boundaries) or couldn't recover from.
    onCaughtError: (error) => reportError(error, { source: "react-boundary" }),
    onUncaughtError: (error) => reportError(error, { source: "react-uncaught" }),
  }).render(
    <StrictMode>
      <App AuthProvider={AuthProvider} />
    </StrictMode>,
  );
}

/** Last-resort error screen when the app can't start (no React, no stylesheet guaranteed). */
function showStartupError(error: unknown) {
  console.error("Failed to start application:", error);
  reportError(error, { source: "bootstrap" });
  const root = document.getElementById("root");
  if (!root) return;
  const message = error instanceof Error ? error.message : String(error);
  // Built with DOM APIs (no innerHTML / inline handlers) so it works under the
  // strict Content-Security-Policy and can't inject markup from the message.
  const el = <K extends keyof HTMLElementTagNameMap>(tag: K, css: Partial<CSSStyleDeclaration>, text?: string) => {
    const node = document.createElement(tag);
    Object.assign(node.style, css);
    if (text) node.textContent = text;
    return node;
  };
  const wrap = el("div", {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100dvh",
    fontFamily: "system-ui,sans-serif",
  });
  const box = el("div", { textAlign: "center", maxWidth: "420px", padding: "2rem" });
  const button = el(
    "button",
    { padding: ".5rem 1.25rem", borderRadius: ".5rem", border: "1px solid #dce0e5", cursor: "pointer" },
    i18n.t("reload"),
  );
  button.addEventListener("click", () => globalThis.location.reload());
  box.append(
    el("h1", { fontSize: "1.25rem", marginBottom: ".75rem" }, i18n.t("startFailed")),
    el("p", { marginBottom: "1.25rem" }, message),
    button,
  );
  wrap.append(box);
  root.replaceChildren(wrap);
}

try {
  await bootstrap();
} catch (error: unknown) {
  showStartupError(error);
}
